import { DateTime, type DateTime as DateTimeType } from "luxon";
import type { CardFragmentFragment } from "@/api/generated/graphql";
import { CardStatus } from "@/api/generated/graphql";
import { CardProvider, type Card } from "@/composables/useDemoCards";

export function cardExpiryFromDate(date: DateTimeType): string {
  return date.toFormat("MM/yyyy");
}

/** Cuscal maskedPAN shape: first4 + 8 asterisks + last4 */
const CUSCAL_MASKED_PAN = /^(\d{4})\*{8}(\d{4})$/;

function normalizePanValue(value: string): string {
  return value.replace(/\s/g, "");
}

export function parseMaskedPan(value: string): { first4: string; last4: string } | null {
  const normalized = normalizePanValue(value);
  const cuscalMatch = normalized.match(CUSCAL_MASKED_PAN);
  if (cuscalMatch) {
    return { first4: cuscalMatch[1], last4: cuscalMatch[2] };
  }

  const digits = normalized.replace(/\D/g, "");
  if (digits.length >= 4) {
    return {
      first4: digits.slice(0, 4),
      last4: digits.slice(-4),
    };
  }

  return null;
}

export function formatCardNumberHidden(maskedPan: string): string {
  const parsed = parseMaskedPan(maskedPan);
  if (!parsed) {
    return maskedPan;
  }
  return `**** **** **** ${parsed.last4}`;
}

export function formatCardNumberVisible(maskedPan: string): string {
  const parsed = parseMaskedPan(maskedPan);
  if (!parsed) {
    return maskedPan;
  }

  const normalized = normalizePanValue(maskedPan);
  if (CUSCAL_MASKED_PAN.test(normalized)) {
    return `${parsed.first4} **** **** ${parsed.last4}`;
  }

  const digits = normalized.replace(/\D/g, "");
  if (digits.length <= 4) {
    return digits;
  }

  const maskedMiddle = "*".repeat(Math.max(0, digits.length - 8));
  const middleGroups = maskedMiddle.match(/.{1,4}/g)?.join(" ") ?? "";
  return `${parsed.first4} ${middleGroups} ${parsed.last4}`.replace(/\s+/g, " ").trim();
}

export function formatCardNumberDisplay(maskedPan: string, revealed: boolean): string {
  return revealed ? formatCardNumberVisible(maskedPan) : formatCardNumberHidden(maskedPan);
}

export function formatCardLabel(card: { type: string; number?: string }): string {
  if (!card.number) {
    return card.type;
  }
  const last4 = parseMaskedPan(card.number)?.last4;
  return last4 ? `${card.type} •••• ${last4}` : card.type;
}

export type ApiCard = CardFragmentFragment;

function mapBrandToProvider(brandCode?: string | null): CardProvider {
  if (brandCode?.toUpperCase().includes("MASTERCARD")) {
    return CardProvider.MASTERCARD;
  }
  return CardProvider.VISA;
}

function parseCardExpiry(expiryDate?: string | null): DateTimeType {
  if (!expiryDate) {
    return DateTime.fromISO("2099-12-01");
  }
  const parsed = DateTime.fromISO(expiryDate.length === 7 ? `${expiryDate}-01` : expiryDate);
  return parsed.isValid ? parsed : DateTime.fromISO("2099-12-01");
}

export function mapApiCardToUi(apiCard: ApiCard): Card | null {
  if (!apiCard.token) {
    return null;
  }

  return {
    id: apiCard.token,
    token: apiCard.token,
    type: apiCard.brandCode || "Card",
    number: apiCard.maskedPAN || "****",
    holder: apiCard.cardHolderName || "",
    expiryDate: parseCardExpiry(apiCard.expiryDate),
    provider: mapBrandToProvider(apiCard.brandCode),
    status: apiCard.status ?? CardStatus.Normal,
  };
}
